import { Currency, CurrencyAmount, Percent, Price, Token } from '@kodiak-finance/sdk-core'
import { computePairAddress, Pair } from '@kodiak-finance/v2-sdk'
import { V2_FACTORY_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import JSBI from 'jsbi'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useV2PositionByPair } from 'pages/Liquidity/ProtocolLists/v2/hooks/useUserV2Positions'
import { useV2Pair } from 'pages/Liquidity/ProtocolLists/v2/hooks/useV2Pair'
import { ReactNode, useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import { REFETCH_POLICY_FAST } from '../../config/RefetchPolicies'
import { PairState } from '../../hooks/useV2Pairs'
import { useCurrencyBalances } from '../connection/hooks'
import { AppState } from '../index'
import { Field, switchFields, typeInput } from './actions'

const ZERO = JSBI.BigInt(0)

export function useMintState(): AppState['mint'] {
  return useAppSelector((state) => state.mint)
}

export function useMintActionHandlers(noLiquidity: boolean | undefined): {
  onFieldAInput: (typedValue: string) => void
  onFieldBInput: (typedValue: string) => void
  onSwitchField: (independentField: Field, typedValue: string, otherTypedValue: string) => void
} {
  const dispatch = useAppDispatch()

  const onFieldAInput = useCallback(
    (typedValue: string) => {
      dispatch(typeInput({ field: Field.CURRENCY_A, typedValue, noLiquidity: noLiquidity === true }))
    },
    [dispatch, noLiquidity]
  )

  const onFieldBInput = useCallback(
    (typedValue: string) => {
      dispatch(typeInput({ field: Field.CURRENCY_B, typedValue, noLiquidity: noLiquidity === true }))
    },
    [dispatch, noLiquidity]
  )

  const onSwitchField = useCallback(
    (independentField: Field, typedValue: string, otherTypedValue: string) => {
      dispatch(
        switchFields({
          independentField,
          typedValue,
          otherTypedValue,
        })
      )
    },
    [dispatch]
  )

  return {
    onFieldAInput,
    onFieldBInput,
    onSwitchField,
  }
}

export function useDerivedMintInfo(
  currencyA: Currency | undefined,
  currencyB: Currency | undefined
): {
  dependentField: Field
  currencies: { [field in Field]?: Currency }
  pair?: Pair | null
  pairState: PairState
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> }
  parsedAmounts: { [field in Field]?: CurrencyAmount<Currency> }
  price?: Price<Currency, Currency>
  noLiquidity?: boolean
  liquidityMinted?: CurrencyAmount<Token>
  poolTokenPercentage?: Percent
  totalSupply: CurrencyAmount<Token> | undefined
  liquidityTokenBalance: CurrencyAmount<Token> | undefined
  positionValueUSD: number | undefined
  error?: ReactNode
} {
  const { account, chainId } = useConnection()

  const { independentField, typedValue, otherTypedValue } = useMintState()
  const dependentField = independentField === Field.CURRENCY_A ? Field.CURRENCY_B : Field.CURRENCY_A

  // tokens
  const currencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: currencyA ?? undefined,
      [Field.CURRENCY_B]: currencyB ?? undefined,
    }),
    [currencyA, currencyB]
  )

  const pairAddress = useMemo(() => {
    if (!currencyA || !currencyB || !chainId) return undefined

    const tokenA = currencyA.wrapped
    const tokenB = currencyB.wrapped
    const factoryAddress = V2_FACTORY_ADDRESSES[chainId]

    if (!tokenA || !tokenB || !factoryAddress) return undefined

    try {
      return computePairAddress({ factoryAddress, tokenA, tokenB }).toLowerCase()
    } catch {
      return undefined
    }
  }, [currencyA, currencyB, chainId])

  const [pairState, pair] = useV2Pair(pairAddress, chainId)
  const { position } = useV2PositionByPair(account, pairAddress, chainId)

  const wrappedTokenA = currencyA?.wrapped
  const wrappedTokenB = currencyB?.wrapped

  const pairMatchesCurrencies = useMemo(() => {
    if (!pair) return true
    if (!wrappedTokenA || !wrappedTokenB) return false
    return pair.involvesToken(wrappedTokenA) && pair.involvesToken(wrappedTokenB)
  }, [pair, wrappedTokenA, wrappedTokenB])

  const safePair = pairMatchesCurrencies ? pair : null

  const liquidityTokenBalance = pairMatchesCurrencies ? position?.liquidityTokenBalance : undefined
  const totalSupply = pairMatchesCurrencies ? position?.totalSupply : undefined
  const valueUSD = pairMatchesCurrencies ? position?.valueUSD : undefined

  const noLiquidity: boolean =
    pairState === PairState.NOT_EXISTS ||
    !pairMatchesCurrencies ||
    Boolean(totalSupply && JSBI.equal(totalSupply.quotient, ZERO)) ||
    Boolean(
      pairState === PairState.EXISTS &&
        safePair &&
        JSBI.equal(safePair.reserve0.quotient, ZERO) &&
        JSBI.equal(safePair.reserve1.quotient, ZERO)
    )

  // balances
  const balances = useCurrencyBalances(
    account ?? undefined,
    useMemo(() => [currencies[Field.CURRENCY_A], currencies[Field.CURRENCY_B]], [currencies]),
    REFETCH_POLICY_FAST
  )
  const currencyBalances: { [field in Field]?: CurrencyAmount<Currency> } = {
    [Field.CURRENCY_A]: balances[0],
    [Field.CURRENCY_B]: balances[1],
  }

  // amounts
  const independentAmount: CurrencyAmount<Currency> | undefined = tryParseCurrencyAmount(
    typedValue,
    currencies[independentField]
  )
  const dependentAmount: CurrencyAmount<Currency> | undefined = useMemo(() => {
    if (noLiquidity) {
      if (otherTypedValue && currencies[dependentField]) {
        return tryParseCurrencyAmount(otherTypedValue, currencies[dependentField])
      }
      return undefined
    } else if (independentAmount) {
      // we wrap the currencies just to get the price in terms of the other token
      const wrappedIndependentAmount = independentAmount?.wrapped
      const [tokenA, tokenB] = [wrappedTokenA, wrappedTokenB]
      if (tokenA && tokenB && wrappedIndependentAmount && safePair) {
        const dependentCurrency = dependentField === Field.CURRENCY_B ? currencyB : currencyA
        const dependentTokenAmount =
          dependentField === Field.CURRENCY_B
            ? safePair.priceOf(tokenA).quote(wrappedIndependentAmount)
            : safePair.priceOf(tokenB).quote(wrappedIndependentAmount)
        return dependentCurrency?.isNative
          ? CurrencyAmount.fromRawAmount(dependentCurrency, dependentTokenAmount.quotient)
          : dependentTokenAmount
      }
      return undefined
    } else {
      return undefined
    }
  }, [
    noLiquidity,
    otherTypedValue,
    currencies,
    dependentField,
    independentAmount,
    currencyA,
    currencyB,
    safePair,
    wrappedTokenA,
    wrappedTokenB,
  ])

  const parsedAmounts: { [field in Field]: CurrencyAmount<Currency> | undefined } = useMemo(() => {
    return {
      [Field.CURRENCY_A]: independentField === Field.CURRENCY_A ? independentAmount : dependentAmount,
      [Field.CURRENCY_B]: independentField === Field.CURRENCY_A ? dependentAmount : independentAmount,
    }
  }, [dependentAmount, independentAmount, independentField])

  const price = useMemo(() => {
    if (noLiquidity) {
      const { [Field.CURRENCY_A]: currencyAAmount, [Field.CURRENCY_B]: currencyBAmount } = parsedAmounts
      if (currencyAAmount?.greaterThan(0) && currencyBAmount?.greaterThan(0)) {
        const value = currencyBAmount.divide(currencyAAmount)
        return new Price(currencyAAmount.currency, currencyBAmount.currency, value.denominator, value.numerator)
      }
      return undefined
    } else {
      if (!safePair || !wrappedTokenA) return undefined
      return safePair.priceOf(wrappedTokenA)
    }
  }, [currencyA, noLiquidity, parsedAmounts, safePair, wrappedTokenA])

  // liquidity minted
  const liquidityMinted = useMemo(() => {
    const { [Field.CURRENCY_A]: currencyAAmount, [Field.CURRENCY_B]: currencyBAmount } = parsedAmounts
    const [tokenAmountA, tokenAmountB] = [currencyAAmount?.wrapped, currencyBAmount?.wrapped]
    if (safePair && totalSupply && tokenAmountA && tokenAmountB) {
      try {
        return safePair.getLiquidityMinted(totalSupply, tokenAmountA, tokenAmountB)
      } catch (error) {
        console.error(error)
        return undefined
      }
    } else {
      return undefined
    }
  }, [parsedAmounts, safePair, totalSupply])

  const poolTokenPercentage = useMemo(() => {
    if (liquidityMinted && totalSupply) {
      return new Percent(liquidityMinted.quotient, totalSupply.add(liquidityMinted).quotient)
    } else {
      return undefined
    }
  }, [liquidityMinted, totalSupply])

  let error: ReactNode | undefined
  if (!account) {
    error = <>Connect Wallet</>
  }

  if (pairState === PairState.INVALID) {
    error = error ?? <>Invalid pair</>
  }

  if (!parsedAmounts[Field.CURRENCY_A] || !parsedAmounts[Field.CURRENCY_B]) {
    error = error ?? <>Enter an amount</>
  }

  const { [Field.CURRENCY_A]: currencyAAmount, [Field.CURRENCY_B]: currencyBAmount } = parsedAmounts

  if (currencyAAmount && currencyBalances?.[Field.CURRENCY_A]?.lessThan(currencyAAmount)) {
    error = <>Insufficient {currencies[Field.CURRENCY_A]?.symbol} balance</>
  }

  if (currencyBAmount && currencyBalances?.[Field.CURRENCY_B]?.lessThan(currencyBAmount)) {
    error = <>Insufficient {currencies[Field.CURRENCY_B]?.symbol} balance</>
  }

  return {
    dependentField,
    currencies,
    pair: safePair,
    pairState,
    currencyBalances,
    parsedAmounts,
    price,
    noLiquidity,
    liquidityMinted,
    totalSupply,
    liquidityTokenBalance,
    positionValueUSD: valueUSD,
    poolTokenPercentage,
    error,
  }
}
